import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { HelpCircle, Lock, TriangleAlert } from 'lucide-react'
import { apiClient } from '@/services/api'
import { useSessionStore } from '@/stores'
import { formatCurrency } from '@utils'
import { getCardIconPath } from '@utils/image'
import {
  cvvLength,
  detectCardBrand,
  formatCardNumber,
  formatExpiry,
  isTestCardNumber,
  isTestCustomer,
  isValidCardNumber,
} from '@/utils/card'
import { useOmpActions } from '../ompActions'
import OmpButton from '../components/OmpButton'
import OmpField from '../components/OmpField'
import { findRebillPaymentInput } from '../utils/portalActions'
import { resolveProductName } from '../utils/omp'
import OmpProductImage from '../components/OmpProductImage'

interface RebillSummaryItem {
  id: number
  name: string
  quantity: number
  unit_price: number
  total_price: number
  rebill_discount: number
  selected?: boolean
  order?: { currency?: string }
}

const schema = z
  .object({
    number: z.string().min(1, 'Card number is required'),
    expiry: z.string().min(1, 'Required'),
    cvv_code: z.string().min(1, 'Required'),
    is_default: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    const brand = detectCardBrand(data.number)
    const customer = (useSessionStore.getState().session as any)?.customer
    const testBypass = !!customer?.is_test || isTestCustomer(customer) || isTestCardNumber(data.number)
    if (!testBypass && !isValidCardNumber(data.number)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Please enter a valid card number', path: ['number'] })
    }
    const digits = data.expiry.replace(/\D/g, '')
    const month = parseInt(digits.slice(0, 2), 10)
    const year = digits.length >= 4 ? 2000 + parseInt(digits.slice(2, 4), 10) : NaN
    if (!month || month < 1 || month > 12 || !year) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a valid expiry (MM/YY)', path: ['expiry'] })
    } else {
      const now = new Date()
      if (year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Card has expired', path: ['expiry'] })
      }
    }
    const cvvLen = cvvLength(brand)
    if (!new RegExp(`^\\d{${cvvLen}}$`).test(data.cvv_code)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Enter a valid ${cvvLen}-digit code`, path: ['cvv_code'] })
    }
  })

type PaymentUpdateFormData = z.infer<typeof schema>

const LOCK_HINT = 'Your card number is encrypted before it is sent'

interface OmpPaymentUpdateDrawerProps {
  session: any
  item: any
  /** Last submit failure, shown in place so the card can be corrected. */
  error?: string | null
  onSubmit: (input: any, data: Record<string, any>) => void
}

const OmpPaymentUpdateDrawer = ({ session, item, error, onSubmit }: OmpPaymentUpdateDrawerProps) => {
  const { busy } = useOmpActions()
  const input = useMemo(() => findRebillPaymentInput(session) ?? (session?.valid_inputs as any[])?.[0], [session])
  const [items, setItems] = useState<RebillSummaryItem[]>([])
  const [loading, setLoading] = useState(true)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<PaymentUpdateFormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { is_default: true },
  })

  useEffect(() => {
    const inputId = input?.id
    if (!inputId) {
      setLoading(false)
      return
    }
    const loadSummary = async () => {
      setLoading(true)
      try {
        const response = await apiClient.get(`/order_items/${inputId}/rebill_summary/`, {
          params: { latest_cycle: true },
        })
        const rows = (response.data as RebillSummaryItem[]) || []
        // The summary covers every failed item; a card-launched action rebills only its own.
        const scoped = item?.id ? rows.filter(row => Number(row.id) === Number(item.id)) : rows
        setItems((scoped.length ? scoped : rows).map(row => ({ ...row, selected: true })))
      } catch {
        setItems([])
      } finally {
        setLoading(false)
      }
    }
    loadSummary()
  }, [input?.id, item?.id])

  // Line totals less the rebill discount, selected lines only.
  const totals = useMemo(() => {
    let fullPrice = 0
    let discount = 0
    for (const line of items) {
      if (line.selected === false) {
        continue
      }
      fullPrice += Number(line.total_price) || 0
      discount += Number(line.rebill_discount) || 0
    }
    const amount = fullPrice - discount
    return {
      fullPrice,
      discount,
      amount,
      percentOff: fullPrice > 0 ? Math.round((discount / fullPrice) * 100) : 0,
    }
  }, [items])

  const currency = items[0]?.order?.currency || item?.order?.currency
  const productName = resolveProductName(item) || items[0]?.name || ''
  const number = watch('number') || ''
  const brand = useMemo(() => detectCardBrand(number), [number])
  const cvvHint =
    cvvLength(brand) === 4 ? 'The 4-digit code on the front of your card' : 'The 3-digit code on the back of your card'

  const submit = (data: PaymentUpdateFormData) => {
    if (!input) {
      return
    }
    const digits = data.expiry.replace(/\D/g, '')
    onSubmit(input, {
      card: {
        number: data.number.replace(/\s/g, ''),
        exp_month: parseInt(digits.slice(0, 2), 10),
        exp_year: 2000 + parseInt(digits.slice(2, 4), 10),
        cvv_code: data.cvv_code,
        is_default: !!data.is_default,
        is_rebill: true,
      },
      items: items.filter(line => line.selected !== false),
    })
  }

  if (!input) {
    return <p className="omp-payup-empty">This payment update is no longer available.</p>
  }

  // Taking a card with nothing to rebill would charge for an empty basket.
  if (!loading && !items.length) {
    return (
      <p className="omp-payup-empty">
        You don't have any subscriptions eligible for reactivation right now. Please contact support if you think this
        is a mistake.
      </p>
    )
  }

  return (
    <div className="omp-payup">
      <div className="omp-payup-alert" role="alert">
        <TriangleAlert size={24} className="omp-payup-alert-icon" />
        <p>Your subscription is paused due to a payment issue. Update your payment method to continue.</p>
      </div>

      <div className="omp-payup-grid">
        <div className="omp-payup-media">
          <OmpProductImage source={item} alt={productName} className="omp-payup-photo" />
        </div>

        <div className="omp-payup-panel">
          <h2 className="omp-payup-title">{productName}</h2>

          {loading ? (
            <div className="omp-payup-pricing">
              <div className="omp-payup-price-was-row">
                <span className="skeleton omp-payup-skel-was" />
                <span className="skeleton omp-payup-skel-save" />
              </div>
              <span className="skeleton omp-payup-skel-now" />
            </div>
          ) : (
            totals.amount > 0 && (
              <div className="omp-payup-pricing">
                {totals.discount > 0 && (
                  <div className="omp-payup-price-was-row">
                    <span className="omp-payup-price-was">{formatCurrency(totals.fullPrice, currency)}</span>
                    {totals.percentOff > 0 && <span className="omp-payup-save">SAVE {totals.percentOff}%</span>}
                  </div>
                )}
                <p className="omp-payup-price-now">
                  {totals.discount > 0 ? 'Now ' : ''}
                  {formatCurrency(totals.amount, currency)}
                </p>
              </div>
            )
          )}

          <form className="omp-payup-form" onSubmit={handleSubmit(submit)}>
            <div className="omp-payup-card">
              <OmpField label="Card number" error={errors.number?.message}>
                <div className="omp-card-number-field">
                  <img
                    src={getCardIconPath(brand || 'credit-card')}
                    alt=""
                    className="omp-card-number-brand"
                    onError={e => {
                      ;(e.target as HTMLImageElement).src = '/assets/icons/credit-card.svg'
                    }}
                  />
                  <input
                    className="form-control"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    placeholder="1234 1234 1234 1234"
                    value={number}
                    onChange={e =>
                      setValue('number', formatCardNumber(e.target.value, brand), { shouldValidate: true })
                    }
                  />
                  <span className="omp-card-number-lock" title={LOCK_HINT} aria-label={LOCK_HINT}>
                    <Lock size={16} />
                  </span>
                </div>
              </OmpField>

              <div className="omp-form-grid">
                <OmpField label="Expiration" error={errors.expiry?.message}>
                  <input
                    className="form-control"
                    inputMode="numeric"
                    autoComplete="cc-exp"
                    placeholder="MM/YY"
                    value={watch('expiry') || ''}
                    onChange={e => setValue('expiry', formatExpiry(e.target.value), { shouldValidate: true })}
                  />
                </OmpField>
                <OmpField label="Security code" error={errors.cvv_code?.message}>
                  <div className="omp-cvv-field">
                    <input
                      className="form-control"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      placeholder="CVV"
                      maxLength={cvvLength(brand)}
                      {...register('cvv_code')}
                    />
                    <span className="omp-cvv-help" title={cvvHint} aria-label={cvvHint}>
                      <HelpCircle size={16} />
                    </span>
                  </div>
                </OmpField>
              </div>
            </div>

            <label className="omp-checkbox">
              <input type="checkbox" {...register('is_default')} />
              <span>Set as default Payment Method</span>
            </label>

            {error && (
              <p className="omp-payup-error" role="alert">
                <TriangleAlert size={18} />
                {error}
              </p>
            )}

            <OmpButton variant="primary" fullWidth type="submit" disabled={!isValid || loading || busy}>
              {totals.discount > 0 ? 'Update and Apply Discount' : 'Update Payment Method'}
            </OmpButton>

            <div className="omp-secure-note">
              <Lock size={18} />
              <div>
                <p className="omp-secure-note-title">Secure Connection</p>
                <p className="omp-secure-note-sub">Your information is safe and secure</p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default OmpPaymentUpdateDrawer
